import { notEmpty } from "@orb-labs/types";
import { BIGINT_SCALE_FACTOR } from "../bullrun/chartUtils.js";
const SCALE = BIGINT_SCALE_FACTOR;
/** Reject OHLC fields more than this many× away from the reference price (e.g. provider bad pair). */
export const OHLC_MAX_DEVIATION_RATIO = 100;
/** Need at least this many OHLC bars before rendering candlesticks (sparse buckets look broken). */
export const MIN_OHLC_BARS_FOR_CANDLES = 3;
function scaledValue(raw) {
    if (!notEmpty(raw) || raw === "")
        return undefined;
    const value = Number(raw) / SCALE;
    if (!Number.isFinite(value) || value <= 0)
        return undefined;
    return value;
}
function timestampSeconds(pt) {
    const ms = pt.timestamp instanceof Date ? pt.timestamp.getTime() : new Date(pt.timestamp).getTime();
    if (!Number.isFinite(ms))
        return undefined;
    return Math.floor(ms / 1000);
}
function isWithinDeviation(value, reference, maxRatio) {
    const min = reference / maxRatio;
    const max = reference * maxRatio;
    return value >= min && value <= max;
}
function isStructurallyValidOhlc(open, high, low, close) {
    if (high < low)
        return false;
    const bodyLow = Math.min(open, close);
    const bodyHigh = Math.max(open, close);
    const tolerance = Math.max(high, bodyHigh) * 1e-6;
    return bodyLow >= low - tolerance && bodyHigh <= high + tolerance;
}
export function medianPositive(values) {
    const valid = values.filter((value) => Number.isFinite(value) && value > 0);
    if (valid.length === 0)
        return undefined;
    const sorted = [...valid].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}
export function ohlcToCandleBars(points, options) {
    const maxRatio = options?.maxDeviationRatio ?? OHLC_MAX_DEVIATION_RATIO;
    const referencePrice = options?.referencePrice;
    const seen = new Set();
    const result = [];
    for (const pt of points) {
        const ts = timestampSeconds(pt);
        if (!notEmpty(ts) || seen.has(ts))
            continue;
        seen.add(ts);
        const open = scaledValue(pt.open);
        const high = scaledValue(pt.high);
        const low = scaledValue(pt.low);
        const close = scaledValue(pt.close);
        if (!notEmpty(open) || !notEmpty(high) || !notEmpty(low) || !notEmpty(close))
            continue;
        if (!isStructurallyValidOhlc(open, high, low, close))
            continue;
        if (notEmpty(referencePrice)) {
            const fields = [open, high, low, close];
            if (!fields.every((value) => isWithinDeviation(value, referencePrice, maxRatio)))
                continue;
        }
        result.push({
            time: ts,
            open,
            high,
            low,
            close,
            // Synthetic volume for histogram rendering only — not venue trade volume.
            volume: Math.abs(close - open) * 1000,
        });
    }
    return result.sort((a, b) => a.time - b.time);
}
export function pricePointsToLineBars(points) {
    const seen = new Set();
    const sorted = [...points].sort((a, b) => {
        const aTs = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
        const bTs = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
        return aTs - bTs;
    });
    const result = [];
    for (const pt of sorted) {
        const ts = timestampSeconds(pt);
        if (!notEmpty(ts) || seen.has(ts))
            continue;
        seen.add(ts);
        const close = scaledValue(pt.value);
        if (!notEmpty(close))
            continue;
        result.push({
            time: ts,
            open: close,
            high: close,
            low: close,
            close,
            volume: 0,
        });
    }
    return result;
}
function hasVariableCandleBars(bars) {
    return bars.some((bar) => bar.high !== bar.low || bar.open !== bar.close);
}
function referenceFromPriceSeries(price) {
    if (!price?.length)
        return undefined;
    return medianPositive(price.map((pt) => scaledValue(pt.value)).filter((value) => notEmpty(value)));
}
function ohlcClosesToLineBars(bars) {
    return bars.map((bar) => ({
        time: bar.time,
        open: bar.close,
        high: bar.close,
        low: bar.close,
        close: bar.close,
        volume: 0,
    }));
}
/**
 * Picks chart bars for fungible tokens: sanitized OHLC candles when trustworthy,
 * otherwise the price time series (or OHLC closes) rendered as a line chart.
 */
export function resolveTokenChartBars(data) {
    if (!data) {
        return { bars: [], useLineChart: false };
    }
    const priceReference = referenceFromPriceSeries(data.price);
    const ohlcReference = priceReference ??
        medianPositive((data.priceOHLC ?? []).map((pt) => scaledValue(pt.close)).filter((value) => notEmpty(value)));
    let ohlcBars = [];
    if (data.priceOHLC?.length && notEmpty(ohlcReference)) {
        ohlcBars = ohlcToCandleBars(data.priceOHLC, { referencePrice: ohlcReference });
        const ohlcMedian = medianPositive(ohlcBars.map((bar) => bar.close));
        const seriesConsistent = !notEmpty(priceReference) ||
            !notEmpty(ohlcMedian) ||
            isWithinDeviation(ohlcMedian, priceReference, OHLC_MAX_DEVIATION_RATIO);
        if (notEmpty(priceReference) &&
            seriesConsistent &&
            hasVariableCandleBars(ohlcBars) &&
            ohlcBars.length >= MIN_OHLC_BARS_FOR_CANDLES) {
            return { bars: ohlcBars, useLineChart: false };
        }
    }
    if (data.price?.length) {
        const priceBars = pricePointsToLineBars(data.price);
        if (priceBars.length > 0) {
            return { bars: priceBars, useLineChart: true };
        }
    }
    if (ohlcBars.length > 0) {
        return { bars: ohlcClosesToLineBars(ohlcBars), useLineChart: true };
    }
    return { bars: [], useLineChart: false };
}
